// i18n
import { translateInComponent } from 'infras/i18n';
import translations from 'presentations/views/components/pages/apps/CalendarYearPage/i18n.json';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Day, CalendarArray, WeekArray } from 'usecases/app_calendar';
import {
  holiday,
  saturday,
  gray01,
  gray02,
  white,
  gray03,
  whiteFix,
  blackFix,
  primary,
} from 'presentations/views/styles/color';

import {
  mediaMin,
  spacing,
  fontWeight,
  fontSize,
} from 'presentations/views/styles';
import Image from 'presentations/views/components/atoms/Image';
import { formatToWeekDay, getDateTime } from 'infras/datetime';

interface MonthProps {
  weeks: CalendarArray;
  filter?: string;
  isShowToday?: boolean;
}

interface WeekProps {
  week: WeekArray;
  filter?: string;
  isShowToday?: boolean;
}

interface DayProps {
  day: Day;
  wday: number;
  filter?: string;
  isToday: boolean;
}

export default function Component({ weeks, filter, isShowToday }: MonthProps) {
  return (
    <Calendar>
      <tbody>
        <WeekHeader>
          {Array.from({ length: 7 }).map((v, wday) => (
            <DayHeader key={wday}>
              {formatToWeekDay(getDateTime().set({ weekday: wday }))}
            </DayHeader>
          ))}
        </WeekHeader>
        {weeks.map((week: Day[], i: number) => (
          <WeekRow
            key={i}
            week={week}
            filter={filter}
            isShowToday={isShowToday}
          />
        ))}
      </tbody>
    </Calendar>
  );
}

const WeekRow = ({ week, filter, isShowToday }: WeekProps) => (
  <WeekContainer>
    {week.map((day: Day, i: number) => (
      <DayCell
        key={day.day}
        day={day}
        wday={i}
        filter={filter}
        isToday={isShowToday ? day.isToday === true : false}
      />
    ))}
  </WeekContainer>
);

const DayCell = ({ day, wday, filter, isToday }: DayProps) => {
  let dayType: string = '';
  switch (wday) {
    case 0:
      dayType = 'holiday';
      break;
    case 6:
      dayType = 'saturday';
      break;
    default:
      break;
  }

  let isDisabled = false;
  if (filter) {
    if (filter === 'taian' && day.local && day.local.rokuyo !== '大安') {
      isDisabled = true;
    }
  }
  return (
    <DayContainer>
      {day.isCurrentMonth && (
        <DayFilter isDisabled={isDisabled}>
          {isToday ? (
            <DayToday>{day.day}</DayToday>
          ) : (
            <DayNumber dayType={dayType}>{day.day}</DayNumber>
          )}
          {day.local && day.local.rokuyo && <Rokuyo>{day.local.rokuyo}</Rokuyo>}
          {day.local && day.local.holiday && (
            <Holiday>
              <a href={`#${day.local.holiday}`}>{day.local.holiday}</a>
            </Holiday>
          )}
          {day.local && day.local.moonAge && <Moon age={day.local.moonAge} />}
        </DayFilter>
      )}
    </DayContainer>
  );
};

const Moon = ({ age }: { age: number }) => {
  const t = translateInComponent(translations);
  const n = Math.floor(age);
  return (
    <MoonContainer>
      <MoonImage src={`apps/calendar/moon/${n}.png`} alt={`Moon age ${n}`} />
      <MoonAge>
        <span>{t('moonAge')}</span> {Math.floor(age * 10) / 10}
      </MoonAge>
    </MoonContainer>
  );
};

const Calendar = styled.table`
  width: 100%;
`;

const MoonImage = styled(Image)`
  width: 28px;
  height: 28px;
  ${mediaMin.tablet} {
    width: 40px;
    height: 40px;
  }
`;

const MoonAge = styled.div`
  margin-top: ${spacing(4)};
`;

const WeekHeader = styled.tr``;
const DayHeader = styled.th`
  padding: ${spacing(8)};
  font-size: ${fontSize(10)};
  ${(props: any) => css`
    color: ${props.theme.appearance === 'dark' ? gray03(props) : gray02(props)};
  `}
`;

const WeekContainer = styled.tr`
  ${(props: any) => {
    if (props.theme.appearance === 'dark') {
      return css`
        border-top: 1px solid ${blackFix(props)};
      `;
    } else {
      return css`
        border-top: 2px solid ${whiteFix(props)};
      `;
    }
  }}
  &:first-of-type {
    border-top-width: 0px;
  }
`;

const DayContainer = styled.td`
  padding: ${spacing(8)};
  width: calc(426px / 7);
  ${(props: any) => {
    if (props.theme.appearance === 'dark') {
      return css``;
    } else {
      return css`
        border-right: 2px solid ${white(props)};
        background: rgb(237, 246, 246);
      `;
    }
  }}
`;
const DayFilter = styled.div`
  ${({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      isDisabled &&
      css`
        opacity: 0.3;
      `
    );
  }}
`;
const DayNumber = styled.div<{ dayType: string }>`
  text-align: center;
  font-size: ${fontSize(20)};
  ${(props) => {
    switch (props.dayType) {
      case 'holiday':
        return css`
          color: ${holiday(props)};
        `;
      case 'saturday':
        return css`
          color: ${saturday(props)};
        `;
    }
  }}
`;
const DayToday = styled.div`
  text-align: center;
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};
  color: ${whiteFix};
  background-color: ${primary};
  border-radius: 8px;
`;
const Rokuyo = styled.div`
  font-size: ${fontSize(11)};
  text-align: center;
`;

const Holiday = styled.div`
  font-size: ${fontSize(11)};
  text-align: center;
`;

const MoonContainer = styled.div`
  text-align: center;
`;
